import React, { useRef, useState } from "react";
import { Youtube, Upload, Loader2, Trash2, Film, Palette, Clock, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";

export default function References() {
  const { projectId } = useApp();
  const fileRef = useRef();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const { data, refresh } = usePoll(() => (projectId ? api.references(projectId) : Promise.resolve(null)), 3000, [projectId]);
  if (!projectId) return <NoProject title="References need a project" />;
  const refs = data?.references || [];

  const uploadLocal = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    setBusy(true);
    try { await api.addLocalReference(projectId, fd); toast.success("Reference added — analyzing"); refresh(); }
    catch (e) { toast.error("Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const addYt = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try { await api.addYoutubeReference(projectId, url.trim()); toast.success("YouTube reference added (metadata only)"); setUrl(""); refresh(); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  const del = async (rid) => { try { await api.deleteReference(rid); refresh(); } catch (e) {} };

  return (
    <div className="p-8 fade-up space-y-6">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Reference Videos</h1>
        <p className="text-[#94A3B8] text-sm mt-1">Style references only — never copied. YouTube uses public metadata (no footage/music downloaded).</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="studio-card border-dashed border-2 border-[#252A3B] hover:border-[#E6B04E]/50 p-6 flex flex-col items-center justify-center cursor-pointer"
          onClick={() => !busy && fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); uploadLocal(e.dataTransfer.files); }}
          data-testid="reference-dropzone">
          <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={(e) => uploadLocal(e.target.files)} data-testid="reference-file-input" />
          <Upload size={24} className="text-[#E6B04E] mb-2" />
          <div className="font-semibold text-sm">Upload local reference</div>
          <div className="metric-label mt-1" style={{ fontSize: 9 }}>analyzes pace, cuts, motion & color</div>
        </div>
        <div className="studio-card p-6 flex flex-col justify-center gap-3">
          <div className="flex items-center gap-2"><Youtube size={18} className="text-[#EF4444]" /><span className="font-semibold text-sm">Add YouTube reference</span></div>
          <div className="flex gap-2">
            <input className="studio-input" placeholder="https://youtube.com/watch?v=…" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="youtube-url-input" />
            <button className="btn btn-gold" onClick={addYt} disabled={busy} data-testid="btn-add-youtube">{busy ? <Loader2 size={16} className="animate-spin" /> : "Add"}</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {refs.map((r) => {
          const sp = r.style_profile;
          return (
            <div key={r.id} className="studio-card p-5" data-testid={`reference-${r.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  {r.kind === "youtube" ? <Youtube size={16} className="text-[#EF4444]" /> : <Film size={16} className="text-[#00F0FF]" />}
                  <span className="font-semibold text-sm truncate max-w-[180px]">{r.name}</span>
                </div>
                <button onClick={() => del(r.id)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 size={15} /></button>
              </div>
              {r.status === "analyzing" ? (
                <div className="flex items-center gap-2 text-sm text-[#E6B04E]"><Loader2 size={14} className="animate-spin" /> Analyzing…</div>
              ) : sp ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2"><Zap size={13} className="text-[#E6B04E]" /> Pace: <b>{sp.pace}</b></div>
                  <div className="flex items-center gap-2"><Clock size={13} className="text-[#00F0FF]" /> Avg shot: <b>{sp.avg_shot_duration ?? "—"}s</b></div>
                  <div className="flex items-center gap-2"><Palette size={13} className="text-[#8B5CF6]" /> Color: <b>{sp.color_style}</b></div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {sp.slow_motion && <span className="tech-badge">slow-mo</span>}
                    {sp.speed_ramps && <span className="tech-badge">speed ramps</span>}
                    <span className="tech-badge">{sp.transition_style}</span>
                    <span className="tech-badge">{sp.emotional_pacing}</span>
                  </div>
                </div>
              ) : <div className="text-[#64748B] text-sm">No profile</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
